import type { FormErrorEvent } from "#ui/types";

/**
 * Memfokuskan field form pertama yang memiliki error lalu menggulirkannya ke viewport.
 *
 * Jika elemen target tidak ditemukan, utilitas ini akan menampilkan toast error
 * sebagai fallback agar pengguna tetap mendapat umpan balik.
 *
 * @param event Event error dari komponen form
 * @returns Promise yang selesai setelah proses fokus/scroll selesai dijalankan
 */
export async function highlightErrorField(event: FormErrorEvent): Promise<void> {
  const { env } = useSiteConfig();

  // Tampilkan detail error di development agar debugging form lebih mudah.
  if (env === "development") {
    console.warn(event);
  }

  // Tidak perlu melakukan apa-apa bila form tidak mengandung error.
  if (!event?.errors?.length) {
    return;
  }

  const toastNotification = useToastNotification();
  const firstError = getFirstErrorWithId(event.errors);

  if (!firstError?.id) {
    toastNotification.add("error", {
      title: "Form Tidak Valid",
      description: firstError?.message || "Terjadi kesalahan pada form",
    });

    return;
  }

  const errorElement = document.getElementById(firstError.id);

  if (errorElement) {
    focusAndScrollIntoView(errorElement);
  }
  else {
    toastNotification.add("error", {
      title: "Form Tidak Valid",
      description: firstError.message || "Terjadi kesalahan pada form",
    });
  }
}

/**
 * Mengurutkan daftar error dan mengambil error pertama yang memiliki `id`.
 *
 * Error tanpa `id` didorong ke belakang karena tidak bisa difokuskan lewat DOM.
 *
 * @param errors Daftar error hasil validasi form
 * @returns Error pertama yang punya `id`, bila ada
 */
function getFirstErrorWithId(
  errors: FormErrorEvent["errors"],
): FormErrorEvent["errors"][number] | undefined {
  return errors.sort((a, b) => (a.id ? (b.id ? 0 : -1) : b.id ? 1 : 0))[0];
}

/**
 * Memfokuskan elemen target lalu menggulirkannya ke tengah viewport.
 *
 * @param element Elemen DOM target
 */
function focusAndScrollIntoView(element: HTMLElement): void {
  element.focus();
  element.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "center",
  });
}
